import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  extractLanguageFromUrl, 
  getCanonicalUrl, 
  getHreflangUrls
} from '../utils/urlNormalization';

const SEO = ({ 
  title, 
  description,
  image = 'https://res.cloudinary.com/dxlhv2mji/image/upload/c_scale,w_800,f_auto,q_auto:good/v1739841939/Jardim_da_Amazo%CC%82nia-3434_ta901j.webp',
  keywords = '',
  noindex = false,
  canonicalUrl = null,
  youtubeUrl = null
}) => {
  const siteName = 'Jardim Amazônia Lodge';
  
  // Extract language and path from current URL
  const langInfo = extractLanguageFromUrl();
  const currentLang = langInfo.language;
  const currentPath = langInfo.path;
  
  // Generate canonical URL
  const languageSpecificCanonical = canonicalUrl || getCanonicalUrl(currentPath, currentLang);
  
  // Generate hreflang URLs
  const hreflangUrls = getHreflangUrls(currentPath);

  // Define supported languages
  const supportedLanguages = ['en', 'pt'];

  // Structured data for better search engine understanding
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: siteName,
    description,
    image,
    url: languageSpecificCanonical,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'BR',
      addressRegion: 'Mato Grosso'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -13.2697,
      longitude: -56.7179
    },
    touristType: [
      'Birdwatchers',
      'Nature Enthusiasts',
      'Researchers',
      'Wildlife Photographers'
    ],
    amenityFeature: [
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Birdwatching Trails',
        value: true
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Research Facilities',
        value: true
      }
    ]
  };

  return (
    <Helmet htmlAttributes={{ lang: currentLang }}>
      {/* Basic meta tags */}
      <title>{`${title} | ${siteName} - Amazon Birdwatching & Research Station`}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Indexing directives */}
      {noindex ? (
        <>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
          <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
          <meta name="googlebot-news" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
          <meta name="googlebot-image" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        </>
      ) : (
        <>
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        </>
      )}
      
      {/* Canonical URL - only for indexable pages */}
      {!noindex && <link rel="canonical" href={languageSpecificCanonical} />}

      {/* Language alternates - only for indexable pages */}
      {!noindex && (
        <>
          {supportedLanguages.map(lang => (
            <link 
              key={`hreflang-${lang}`}
              rel="alternate" 
              hrefLang={lang} 
              href={hreflangUrls[lang]} 
            />
          ))}
          <link rel="alternate" hrefLang="x-default" href={hreflangUrls['x-default']} />
        </>
      )}

      {/* Additional meta tags for search engines */}
      <meta name="language" content={currentLang === 'pt' ? 'Portuguese' : 'English'} />
      <meta name="revisit-after" content="7 days" />

      {/* Open Graph meta tags - only for indexable pages */}
      {!noindex && (
        <>
          <meta property="og:title" content={`${title} | ${siteName}`} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={image} />
          <meta property="og:url" content={languageSpecificCanonical} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={siteName} />
          <meta property="og:locale" content={currentLang === 'pt' ? 'pt_BR' : 'en_US'} />
          <meta property="og:locale:alternate" content={currentLang === 'pt' ? 'en_US' : 'pt_BR'} />
          <meta property="og:see_also" content="https://www.linkedin.com/company/jardim-da-amazonia/" />
          {youtubeUrl && <meta property="og:see_also" content={youtubeUrl} />}
          <link rel="me" href="https://www.linkedin.com/company/jardim-da-amazonia/" />
          {youtubeUrl && <link rel="me" href={youtubeUrl} />}
        </>
      )}

      {/* Twitter Card meta tags - only for indexable pages */}
      {!noindex && (
        <>
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${title} | ${siteName}`} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={image} />
        </>
      )}

      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO; 